<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader @back="trackUpdatePaymentPaywallBackClick" />
			</LayoutFlex>
		</template>

		<template #content>
			<LayoutFlex padding="var(--size-8) var(--size-20) var(--size-24)" gap="var(--size-24)">
				<LayoutFlex padding="0" gap="var(--size-12)" align-items="flex-start">
					<Text font="--heading-h3" color="--text-dark-primary">Update your payment method</Text>
					<Text font="--text-16-22-reg" color="--text-dark-secondary">
						Choose the payment method you'd like us to use for future Kova subscription payments.
					</Text>
				</LayoutFlex>

				<!-- Dev-bypass preview: the widget can't run against the mocked transport (it needs
				     a real Stripe client session), so by default the bypass renders static
				     lookalikes of the funnel-identical block and a canned happy-path demo. Setting
				     VITE_DEV_BYPASS_BILLING_CUSTOMER_ID (a customer with a live signup session on
				     the dev engine) skips the lookalikes and mounts the real widget instead.
				     Prod builds tree-shake this branch away. -->
				<LayoutFlex
					v-if="devBypassEnabled && !devBypassBillingCustomerId"
					padding="0"
					gap="var(--size-12)"
					min-width="100%"
				>
					<LayoutFlex padding="0" gap="var(--size-8)" min-width="100%">
						<button type="button" class="update-payment__wallet" aria-label="Apple Pay" @click="demoSubmit">
							<ApplePayIcon />
						</button>
						<button type="button" class="update-payment__wallet" aria-label="Google Pay" @click="demoSubmit">
							<GooglePayIcon />
						</button>
					</LayoutFlex>

					<div class="update-payment__divider"><span>OR</span></div>

					<LayoutFlex
						padding="var(--size-16)"
						gap="var(--size-12)"
						min-width="100%"
						border-width="1.5px"
						border-style="solid"
						border-color="--stroke-dark-low"
						corner-radius="var(--size-12)"
					>
						<LayoutFlex
							flex-direction="row"
							justify-content="space-between"
							align-items="center"
							padding="0"
							min-width="100%"
						>
							<Text font="--text-14-20-reg" color="--text-dark-primary">Credit card</Text>
							<img :src="paymentSystems" alt="Payment systems" height="20" />
						</LayoutFlex>

						<div class="update-payment__fields">
							<input class="update-payment__input" type="text" inputmode="numeric" placeholder="1234 1234 1234 1234" />
							<div class="update-payment__fields-row">
								<input class="update-payment__input" type="text" inputmode="numeric" placeholder="MM / YY" />
								<input class="update-payment__input" type="text" inputmode="numeric" placeholder="CVC" />
							</div>
						</div>

						<Button class="update-payment__confirm" full-width @ds:click="demoSubmit">
							<Text font="--text-16-18-semi">Confirm</Text>
						</Button>
					</LayoutFlex>
				</LayoutFlex>

				<BillingClient
					v-else-if="hasBillingCustomer"
					v-slot="{ paymentClients }"
					@loading="onLoading"
					@ready="onReady"
					@submit="onSubmit"
					@success="onSuccess"
					@cancelled="onCancelled"
					@error="onError"
				>
					<!-- med-web-funnel's payment form (GlpPaymentMethodForm, theme "app") ported 1:1:
					     wallet pills above, OR divider, then the card section with web-billing's
					     hosted Stripe fields and the Confirm inside (via the widget's #button slot). -->
					<LayoutFlex padding="0" gap="var(--size-12)" min-width="100%">
						<LayoutFlex
							v-if="paymentClients.apple_pay || paymentClients.google_pay"
							v-show="isAnyWalletAvailable(paymentClients)"
							padding="0"
							gap="var(--size-8)"
							min-width="100%"
						>
							<ApplePayButton
								v-if="paymentClients.apple_pay"
								v-show="paymentClients.apple_pay.available"
								:config="paymentClients.apple_pay"
							/>
							<GooglePayButton
								v-if="paymentClients.google_pay"
								v-show="paymentClients.google_pay.available"
								:config="paymentClients.google_pay"
							/>
						</LayoutFlex>

						<div v-show="isAnyWalletAvailable(paymentClients)" class="update-payment__divider"><span>OR</span></div>

						<LayoutFlex
							padding="var(--size-16)"
							gap="var(--size-12)"
							min-width="100%"
							border-width="1.5px"
							border-style="solid"
							border-color="--stroke-dark-low"
							corner-radius="var(--size-12)"
						>
							<LayoutFlex
								flex-direction="row"
								justify-content="space-between"
								align-items="center"
								padding="0"
								min-width="100%"
							>
								<Text font="--text-14-20-reg" color="--text-dark-primary">Credit card</Text>
								<img :src="paymentSystems" alt="Payment systems" height="20" />
							</LayoutFlex>

							<CardForm v-if="paymentClients.card" :config="paymentClients.card" />
							<div v-else class="update-payment__form-skeleton" aria-hidden="true" />
						</LayoutFlex>
					</LayoutFlex>
				</BillingClient>

				<template v-else-if="store.loading">
					<Text font="--text-16-22-reg" color="--text-dark-secondary">Loading…</Text>
				</template>

				<template v-else-if="store.error">
					<!-- The bootstrap subscription fetch failed (matters for the post-login deep-link
					     entry); a successful retry reactively mounts the payment form. -->
					<LayoutFlex
						padding="var(--size-16)"
						gap="var(--size-12)"
						background="--background-white"
						corner-radius="var(--size-24)"
					>
						<Text font="--text-18-22-bold" color="--text-dark-primary">Couldn't load your subscription</Text>
						<Text font="--text-14-20-reg" color="--text-dark-secondary">{{ store.error }}</Text>
						<Button hierarchy="secondary" full-width @ds:click="store.fetchSubscription()">
							<Text font="--text-16-18-semi">Try again</Text>
						</Button>
					</LayoutFlex>
				</template>

				<template v-else>
					<!-- No billing customer id on the subscription — the change endpoints can't be
					     called. Expected only for accounts without a subscription on file. -->
					<LayoutFlex
						padding="var(--size-16)"
						gap="var(--size-12)"
						background="--background-white"
						corner-radius="var(--size-24)"
					>
						<Text font="--text-16-22-reg" color="--text-dark-primary">
							We couldn't open the payment form for your account. Please contact support.
						</Text>
					</LayoutFlex>
				</template>
			</LayoutFlex>
		</template>
	</LayoutScreen>

	<!-- Verifying takeover while the change call / 3DS is in flight. A fullscreen DS Modal
	     (teleported, z-index 1000 — under the 3DS iframe's 10000) hosts a LayoutScreen, so
	     the paywall and its PaymentClient stay mounted underneath (a route swap would abort
	     the flow). -->
	<Modal :visible="processing" fullscreen animation="none">
		<VerifyingScreen />
	</Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type {
	IErrorEvent,
	ILoadingEvent,
	ISubmitEvent,
	ISuccessEvent,
	PaymentClients,
} from '@simple-life-app/web-billing'
import { Button, LayoutFlex, LayoutScreen, Modal, Text } from '@simple-life-app/growth-ui-kova'
import CancelHeader from '@/components/CancelHeader.vue'
import ApplePayButton from '@/components/payment/ApplePayButton.vue'
import ApplePayIcon from '@/components/payment/ApplePayIcon.vue'
import BillingClient from '@/components/payment/BillingClient.vue'
import CardForm from '@/components/payment/CardForm.vue'
import GooglePayButton from '@/components/payment/GooglePayButton.vue'
import GooglePayIcon from '@/components/payment/GooglePayIcon.vue'
import VerifyingScreen from '@/components/payment/VerifyingScreen.vue'
import { devBypassBillingCustomerId, devBypassEnabled } from '@/auth/devBypass'
import { getSubscription } from '@/api/medBilling'
import { useSubscriptionStore } from '@/store/subscription'
import {
	CANCELLATION_PAYMENT_ENTRY_POINTS,
	trackUpdatePaymentMethodPaywallDone,
	trackUpdatePaymentMethodPaywallShow,
	trackUpdatePaymentPaywallBackClick,
	trackUpdatePaymentScreenView,
	type UpdatePaymentEntryPoint,
} from '@/analytics/events'
import paymentSystems from '@/assets/payment/payment-systems.png'

const route = useRoute()
const router = useRouter()
const store = useSubscriptionStore()

const processing = ref(false)
// True between a submit (card Confirm / wallet sheet confirm) and its outcome. Errors
// OUTSIDE an attempt are init/load failures (e.g. a wallet's Express Checkout
// `loaderror` fires on mount with no user action) and are handled separately.
const attemptActive = ref(false)
const paywallShowTracked = ref(false)

const hasBillingCustomer = computed(() => Boolean(store.subscription?.customer_id))

// Entry-point taxonomy (owner decision 2026-08-14): the failed screen's retry arrives
// with ?entry=unsuccess; the /status button marks its navigation with a history-state
// `entry` (survives refresh, invisible in the URL); anything else — a CRM/email deep
// link, a bookmark, the post-login redirect — reports 'direct'.
const entryPoint = computed<UpdatePaymentEntryPoint>(() => {
	if (route.query.entry === 'unsuccess') return 'update_payment_method_unsuccess_screen'
	const entry = window.history.state?.entry
	if (entry === 'subscription_details_button') return 'subscription_details_button'
	// The commitment cancel flow (AL-15098, CR 75 / 76): "Update my payment method" / "Update payment method".
	if (CANCELLATION_PAYMENT_ENTRY_POINTS.includes(entry)) return entry
	return 'direct'
})

function isAnyWalletAvailable(clients: PaymentClients): boolean {
	return Boolean(clients.apple_pay?.available || clients.google_pay?.available)
}

function trackPaywallShow(): void {
	if (paywallShowTracked.value) return
	paywallShowTracked.value = true
	trackUpdatePaymentMethodPaywallShow({
		entry_point: entryPoint.value,
		previous_method: previousMethod(),
		// Configured methods, per the AL-14225 spec example. Wallet availability is
		// device-dependent and settles async — refine once the SDK release lands.
		available_methods: ['card', 'apple_pay', 'google_pay'],
	})
}

// Analytics slug for the current method: the DTO uses 'payment_card' where the
// spec says 'card'; wallet slugs already match.
function previousMethod(): string | undefined {
	const raw = store.subscription?.payment_method
	if (!raw) return undefined
	return raw === 'payment_card' ? 'card' : raw
}

let paywallShowFallbackTimer: ReturnType<typeof setTimeout> | undefined

onMounted(() => {
	// Fallback so the Show event isn't lost if the widget never reaches `ready`
	// (network failure straight into the error screen).
	paywallShowFallbackTimer = setTimeout(trackPaywallShow, 3000)
})

onUnmounted(() => {
	// Don't report a paywall Show after the user has already left the screen.
	clearTimeout(paywallShowFallbackTimer)
})

function onReady(): void {
	trackPaywallShow()
}

function onLoading(payload: ILoadingEvent): void {
	const active = Boolean(payload?.value)
	if (active && !processing.value) trackUpdatePaymentScreenView('UPDATE_PAYMENT_METHOD_LOADING_SCREEN')
	processing.value = active
}

function onSubmit(payload: ISubmitEvent): void {
	attemptActive.value = true
	trackUpdatePaymentMethodPaywallDone({
		entry_point: entryPoint.value,
		selected_method: payload.paymentMethod,
		previous_method: previousMethod(),
	})
}

function onSuccess(_payload: ISuccessEvent): void {
	// The default method changed — refresh the store copy behind the navigation so
	// /status shows the new payment method. Per the state.md convention this must never
	// wipe the stored subscription on a transient failure: call getSubscription()
	// directly (fetchSubscription nulls the store on error) and ignore a null (404).
	void getSubscription()
		.then((refreshed) => {
			if (refreshed) store.setSubscription(refreshed)
		})
		.catch(() => {
			/* keep the existing subscription; /status renders the pre-change method */
		})
	// Keep the verifying overlay up through the route swap (replace: Back from the
	// success screen should not land on a stale paywall mid-flow).
	router.replace('/payment/success')
}

function onError(payload: IErrorEvent): void {
	processing.value = false
	const wasAttempt = attemptActive.value
	attemptActive.value = false
	if (!wasAttempt) {
		// No user action yet → init/load failure. A wallet `loaderror` is benign (the
		// button hides itself via `available`; the card path still works) — stay put.
		// Anything else (client-session failure, Stripe.js/card init failure) means the
		// form can never become usable: surface the terminal screen, whose retry
		// remounts the widget and re-runs the client-session call.
		if (payload.paymentMethod === 'apple_pay' || payload.paymentMethod === 'google_pay') return
		router.push('/payment/failed')
		return
	}
	// Post-submit: a Confirm tap with incomplete/invalid card fields surfaces as a
	// widget error with source 'sdk' (Stripe createPaymentMethod validation) — the
	// hosted fields already render inline messages, keep the user on the form. Real
	// payment failures carry source 'api' (backend decline of /payment-method/change,
	// 3DS); wallet payment errors always go to the terminal screen.
	if (payload.paymentMethod === 'card' && payload.source !== 'api') return
	router.push('/payment/failed')
}

function onCancelled(): void {
	// Wallet sheet dismissed by the user — not an error, stay on the paywall.
	attemptActive.value = false
	processing.value = false
}

// Dev-bypass only: canned happy path so the flow (and its analytics sequence) can be
// click-tested in preview.
function demoSubmit(): void {
	trackUpdatePaymentMethodPaywallDone({
		entry_point: entryPoint.value,
		selected_method: 'card',
		previous_method: previousMethod(),
	})
	processing.value = true
	trackUpdatePaymentScreenView('UPDATE_PAYMENT_METHOD_LOADING_SCREEN')
	setTimeout(() => {
		processing.value = false
		router.replace('/payment/success')
	}, 1500)
}
</script>

<style scoped>
/* Everything structural above is growth-ds (LayoutFlex/LayoutScreen/Modal). What remains
   here are the pieces the DS deliberately doesn't cover, all mirroring med-web-funnel's
   GlpPaymentMethodForm 1:1 (see ADR 0013 p.7):
   - wallet pill lookalikes (dev-bypass only; the live pills come from the web-billing
     wrappers in src/components/payment/),
   - the "OR" text divider (no DS component),
   - Stripe-chrome field lookalikes + loading shimmer (the live fields are hosted iframes
     that must be matched, not the DS),
   - a 16px-radius override on Confirm (the funnel submit, not the Kova 20px pill). */

.update-payment__wallet {
	appearance: none;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	margin: 0;
	padding: 10px var(--size-12);
	background: #000;
	color: var(--text-white-primary, #fff);
	border: 1px solid transparent;
	border-radius: var(--size-16);
	cursor: pointer;
}

.update-payment__wallet svg {
	display: block;
}

.update-payment__divider {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: var(--size-4) 0;
	color: var(--text-dark-tertiary);
	font-size: 12px;
	line-height: 16px;
}

.update-payment__divider::before,
.update-payment__divider::after {
	content: '';
	flex: 1;
	height: 2px;
	background: var(--stroke-dark-low);
}

.update-payment__divider span {
	padding: 0 var(--size-12);
}

/* Shimmer placeholder while the hosted card fields initialise; height matches the
   mounted form (two 50px field rows + the Confirm button + the widget's 16px gaps). */
.update-payment__form-skeleton {
	width: 100%;
	height: 182px;
	border-radius: var(--size-12);
	background: linear-gradient(
		90deg,
		var(--background-alpha-low) 25%,
		var(--background-alpha-solid) 50%,
		var(--background-alpha-low) 75%
	);
	background-size: 200% 100%;
	animation: update-payment-shimmer 1.2s ease-in-out infinite;
}

@keyframes update-payment-shimmer {
	to {
		background-position: -200% 0;
	}
}

.update-payment__fields {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: var(--size-16);
}

.update-payment__fields-row {
	display: flex;
	gap: 10px;
}

.update-payment__fields-row > * {
	flex: 1;
	min-width: 0;
}

.update-payment__input {
	appearance: none;
	width: 100%;
	height: 50px;
	padding: 4px 10px;
	background: #fff;
	border: 1px solid rgba(50, 54, 83, 0.3);
	border-radius: 8px;
	color: #323653;
	font-family: sans-serif;
	font-size: 16px;
	outline: none;
	transition: border-color 0.3s;
}

.update-payment__input::placeholder {
	color: #c5c4c7;
}

.update-payment__input:focus {
	border-color: #7d8bf7;
}

.update-payment__confirm {
	border-radius: var(--size-16);
}
</style>
